import { Action, State, StateContext, Store } from '@ngxs/store';
import { CreateStudentRequest, StudentData, DeleteStudentRequest } from '../models/student-data.model';
import { Injectable } from '@angular/core';
import { StudentService } from '../services/student.service';
import { AlertService } from 'src/app/shared/store/alert/alert.service';
import { LocalizationService } from 'src/app/services/localization/localization.service';
import { StudentActions } from './student.actions';
import { MappingUtilsService } from 'src/app/services/utils/mapping-utils.service';
import { StudentForm } from '../models/student-form';

export class StudentStateModel {
  addStudentForm: {
    model: StudentForm;
  };
  isLoading: boolean;
  public error: string;
}

const defaults = {
  addStudentForm: {
    model: null,
  },
  isLoading: false,
  error: null,
};

@State<StudentStateModel>({
  name: 'students',
  defaults,
})
@Injectable()
export class StudentState {
  constructor(
    private studentService: StudentService,
    private alertService: AlertService,
    private localizationService: LocalizationService,
    public store: Store
  ) {}

  @Action(StudentActions.AddStudent)
  addStudent(ctx: StateContext<StudentStateModel>, { exerciseId, user }: StudentActions.AddStudent) {
    const state = ctx.getState();
    const studentForm = state.addStudentForm.model;
    const createRequest = this.convertStudentFormToCreateRequest(studentForm, exerciseId);

    this.alertService
      .confirm(
        this.localizationService.translate(this.getTitle()),
        this.localizationService.translate('students.studentAddConfirm'),
        this.localizationService.translate('buttons.accept'),
        this.localizationService.translate('buttons.cancel'),
        this.localizationService
          .translate('students.studentAddConfirm')
          .replace('{0}', createRequest.name)
          .replace('{1}', createRequest.surname)
          .replace('{2}', createRequest.lastname)
      )
      .then((result) => {
        if (result.value) {
          ctx.patchState({ isLoading: true });
          return this.studentService.addStudent(createRequest, user).subscribe({
            next: (studentId) => {
              ctx.dispatch(
                new StudentActions.AddStudentSuccess(studentForm, studentId)
              );
            },
            error: (error) => {
              ctx.dispatch(new StudentActions.Error(error, createRequest.name));
            },
          });
        } else {
          return null;
        }
      });
  }

  @Action(StudentActions.AddStudentSuccess)
  addStudentSuccess(ctx: StateContext<StudentStateModel>, { }: StudentActions.AddStudentSuccess) {
    ctx.patchState({
      isLoading: false,
    });
  }

  @Action(StudentActions.DeleteStudent)
  deleteStudent(ctx: StateContext<StudentStateModel>, { student, familyId, user }: StudentActions.DeleteStudent) {
    const state = ctx.getState();
    const deleteStudent = this.createDeleteStudentRequest(student, familyId);
    this.alertService
      .confirm(
        this.localizationService.translate(this.getTitle()),
        this.localizationService.translate('students.studentDeleteConfirm'),
        this.localizationService.translate('buttons.accept'),
        this.localizationService.translate('buttons.cancel'),
        this.localizationService
          .translate('students.studentDeleteConfirm')
          .replace('{0}', student.name)
          .replace('{1}', student.surname)
          .replace('{2}', student.lastname)
      )
      .then((result) => {
        if (result.value) {
          ctx.patchState({ isLoading: true });
          return this.studentService
            .deleteStudent(deleteStudent, user)
            .subscribe({
              next: () => {
                ctx.dispatch(
                  new StudentActions.DeleteStudentSuccess(
                    deleteStudent.studentId
                  )
                );
              },
              error: (error) => {
                ctx.dispatch(
                  new StudentActions.Error(error, student.name)
                );
              },
            });
        } else {
          return null;
        }
      });
  }

  @Action(StudentActions.DeleteStudentSuccess)
  deleteStudentSuccess(
    ctx: StateContext<StudentStateModel>,
    { studentId }: StudentActions.DeleteStudentSuccess
  ) {
    ctx.patchState({
      isLoading: false,
    });
  }

  @Action(StudentActions.SetStudentDefaultState)
  setStudentDefaultState(
    ctx: StateContext<StudentStateModel>,
    {}: StudentActions.SetStudentDefaultState
  ) {
    const state = ctx.getState();
    ctx.patchState(defaults);
  }

  @Action(StudentActions.Error)
  onErrorOcurred(
    ctx: StateContext<StudentStateModel>,
    { error, details }: StudentActions.Error
  ) {
    error.replace('{0}', details);
    ctx.patchState({
      error: error,
      isLoading: false,
    });
    MappingUtilsService.showError(this.getTitle(), error, this.alertService, this.localizationService);
  }

  private convertStudentFormToCreateRequest(studentForm: StudentForm, exerciseId: number) : CreateStudentRequest {
    const createStudentRequest = new CreateStudentRequest();
    createStudentRequest.schoolId = studentForm.schoolId;
    createStudentRequest.surname = studentForm.surname;
    createStudentRequest.lastname = studentForm.lastname;
    createStudentRequest.name = studentForm.name;
    createStudentRequest.familyId = studentForm.familyId;
    createStudentRequest.courseId = studentForm.courseId;
    createStudentRequest.classroom = studentForm.classroom;
    createStudentRequest.nacionality = studentForm.nacionality;
    createStudentRequest.birthday = studentForm.birthday;
    createStudentRequest.genderId = studentForm.genderId;
    createStudentRequest.transferPayment = studentForm.transferPayment;
    createStudentRequest.cauCode = studentForm.cauCode;
    createStudentRequest.email = studentForm.email;
    createStudentRequest.card = studentForm.card;
    createStudentRequest.guardianIdentification = studentForm.guardianIdentification;
    createStudentRequest.exerciseId = exerciseId;
    return createStudentRequest;
  }

  private createDeleteStudentRequest(studentData: StudentData, familyId: number) : DeleteStudentRequest {
    const deleteStudentRequest = new DeleteStudentRequest();
    deleteStudentRequest.familyId = familyId;
    deleteStudentRequest.studentId = studentData.studentId;
    return deleteStudentRequest;
  }

  private getTitle() {
    return this.localizationService.translate('students.title');
  }
}
